import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Loading } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { omit } from 'lodash';
import { DataTable, FIELDS, FieldType, Form, TrackableButton, useAlert, useFields, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
// import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import { useMutation } from '@apollo/client';
import { SAVE_FIELD } from '@src/graphql/queries/field';
import { prepareForSave } from '@src/utilities/gqlHelper';
export const Fields = ({ clientId, entityType, filter }) => {
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { items, loading, error } = useFields({
        clientId,
        entityType,
        includeInactive: true,
        isActive: filter?.isActive,
        searchText: filter?.searchText,
    });
    const [selected, setSelected] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const FormValidationFields = useMemo(() => {
        const validationFields = [
            {
                name: 'isUnique',
                label: t('Is Unique?'),
                type: 'boolean',
            },
            {
                name: 'valueFormat',
                label: t('Value Format'),
                type: 'text',
                hidden: selected?.type !== FieldType.DATE && selected?.type !== FieldType.DATE_TIME,
            },
            {
                name: 'minLength',
                label: t('Min Length'),
                type: 'text',
                hidden: selected?.type !== FieldType.STRING,
            },
            {
                name: 'maxLength',
                label: t('Max Length'),
                type: 'text',
                hidden: selected?.type !== FieldType.STRING,
            },
            {
                name: 'matchRegex',
                label: t('Match Regular Expression'),
                type: 'text',
                hidden: selected?.type !== FieldType.STRING,
            },
            {
                name: 'minRange',
                label: t('Min Range'),
                type: 'text',
                hidden: selected?.type !== FieldType.DECIMAL && selected?.type !== FieldType.INTEGER,
            },
            {
                name: 'maxRange',
                label: t('Max Range'),
                type: 'text',
                hidden: selected?.type !== FieldType.DECIMAL && selected?.type !== FieldType.INTEGER,
            },
            {
                name: 'minDate',
                label: t('Min Date'),
                type: 'date',
                hidden: selected?.type !== FieldType.DATE && selected?.type !== FieldType.DATE_TIME,
            },
            {
                name: 'maxDate',
                label: t('Max Date'),
                type: 'date',
                hidden: selected?.type !== FieldType.DATE && selected?.type !== FieldType.DATE_TIME,
            },
        ];
        return validationFields;
    }, [selected, t]);
    const FormFields = useMemo(() => {
        const fields = [
            {
                name: 'name',
                label: t('Name'),
                type: 'text',
            },
            {
                name: 'code',
                label: t('Code'),
                type: 'text',
                disabled: true,
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                disabled: selected?.isSystem ?? false,
            },
            {
                name: 'isRequired',
                label: t('Required?'),
                type: 'boolean',
                disabled: selected?.isSystem ?? false,
            },
            {
                name: 'isLookup',
                label: t('Lookup?'),
                type: 'boolean',
                disabled: selected?.isSystem ?? false,
            },
            {
                name: 'selections',
                label: t('Selections'),
                type: 'textarea',
                plainText: true,
                deserializeValue: (value) => (value ? value.split('\n') : null),
                serializeValue: (value) => value?.join('\n') ?? '',
            },
        ];
        return fields;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, t]);
    const handleValidate = (field) => {
        const errors = {};
        if (!field.name) {
            errors.name = t('Name is required');
        }
        return errors;
    };
    const [saveField] = useMutation(SAVE_FIELD, {
        refetchQueries: (result) => {
            if (result.errors || result.data?.saveField?.__typename === 'MessageResponse') {
                return [];
            }
            return [FIELDS];
        },
    });
    const sanitizeValidations = (validations) => {
        // convert to numbers where needed
        return {
            ...validations,
            isUnique: validations.isUnique?.toString() === 'true',
            minLength: validations.minLength ? Number(validations.minLength) : undefined,
            maxLength: validations.maxLength ? Number(validations.maxLength) : undefined,
            minRange: validations.minRange ? Number(validations.minRange) : undefined,
            maxRange: validations.maxRange ? Number(validations.maxRange) : undefined,
        };
    };
    const handleSave = async (field) => {
        try {
            const result = await saveField({
                variables: {
                    input: {
                        ...omit(prepareForSave(field), ['id', 'createdAt', 'updatedAt', '__typename']),
                        id: field.clientId === clientId ? field.id : null,
                        clientId: clientId ?? 0,
                        isActive: field.isActive.toString() === 'true',
                        isRequired: field.isRequired.toString() === 'true',
                        isSystem: field.isSystem.toString() === 'true',
                        isLookup: field.isLookup.toString() === 'true',
                        validations: prepareForSave(sanitizeValidations(field.validations ?? {})),
                    },
                },
            });
            if (result.data.saveField.__typename === 'MessageResponse') {
                // let the form display the errors
                return result.data.saveField;
            }
            showAlert(t('Field saved'), 'success');
            setSelected(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    return (_jsxs(Loading, { label: 'Fields', loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: items || [], pageSizeOptions: [10, 25], paginationModel: paginationModel, onPaginationModelChange: newModel => setPaginationModel(newModel), columns: [
                    {
                        field: 'name',
                        headerName: t('Name'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'code',
                        headerName: t('Code'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'type',
                        headerName: t('Type'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'isActive',
                        headerName: t('Active?'),
                        valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'isRequired',
                        headerName: t('Required?'),
                        valueGetter: e => (e.row.isRequired ? 'Yes' : 'No'),
                        flex: 0.2,
                        sortable: false,
                    },
                    {
                        field: 'action',
                        headerName: t('Actions'),
                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `field_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelected(row), className: 'margin--right_8' }) })),
                        width: 100,
                        sortable: false,
                    },
                ] }), selected && (_jsx(Form, { title: selected.id ? 'Edit field' : 'Add field', modal: true, obj: selected, fields: FormFields, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelected(null), children: ({ draft, setDraft, errors }) => (_jsx("div", { children: _jsx(Form, { obj: {
                            ...(selected.validations ?? {}),
                        }, fields: FormValidationFields, embedded: true, onChange: value => setDraft({ ...draft, validations: value }), errors: errors }) })) }))] }));
};
